import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { dramas, heroSlides } from "@/mocks/dramas";
import { categoryLabel } from "@/utils/drama";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % heroSlides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused]);

  const slide = heroSlides[active];
  const drama = dramas.find((item) => item.id === slide.id) ?? dramas[0];

  const go = (direction: 1 | -1) => {
    setActive((prev) => (prev + direction + heroSlides.length) % heroSlides.length);
  };

  return (
    <section
      aria-label="热门短剧推荐"
      className="relative w-full overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-[560px] w-full md:h-[min(100vh,760px)]">
        {heroSlides.map((item, index) => (
          <img
            key={item.id}
            src={item.image}
            alt={`${item.kicker}《${dramas.find((d) => d.id === item.id)?.title ?? "热门短剧"}》剧照背景`}
            width={1920}
            height={1080}
            loading={index === 0 ? "eager" : "lazy"}
            className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-1000 ${
              index === active ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
        <div className="hero-mask absolute inset-0" />

        <div className="relative z-10 flex h-full w-full flex-col justify-end px-4 pb-10 md:px-10 md:pb-16">
          <div className="w-full max-w-3xl animate-fade-up">
            <p className="mb-3 flex flex-wrap items-center gap-2 text-[11px] md:text-xs">
              <span className="rounded-full bg-accent-500 px-2.5 py-1 font-semibold text-background-50">
                {slide.kicker}
              </span>
              <span className="rounded-full border border-foreground-50/20 bg-background-950/40 px-2.5 py-1 text-foreground-200 backdrop-blur-sm">
                {categoryLabel(drama.category)}
              </span>
              <span className="flex items-center gap-1 rounded-full border border-primary-400/40 bg-background-950/40 px-2.5 py-1 font-semibold text-primary-300 backdrop-blur-sm">
                <i className="ri-star-fill text-[10px]" aria-hidden="true" />
                {drama.rating.toFixed(1)} 分
              </span>
            </p>

            <h1 className="font-heading text-3xl font-black leading-tight text-foreground-50 md:text-5xl lg:text-6xl">
              {drama.title}
              <span className="mt-2 block font-body text-sm font-normal tracking-wide text-foreground-300 md:mt-3 md:text-base">
                全集高清在线观看 · 剧焰短剧
              </span>
            </h1>

            <p className="mt-3 max-w-xl text-xs leading-relaxed text-foreground-300 md:mt-4 md:text-sm">
              {slide.slogan}
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link
                to={`/drama/${drama.id}?ep=1`}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-950 shadow-lg shadow-primary-950/50 transition-colors hover:bg-primary-400 md:px-6 md:py-3"
              >
                <i className="ri-play-fill text-lg" aria-hidden="true" />
                立即播放
              </Link>
              <Link
                to={`/drama/${drama.id}`}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-foreground-50/25 bg-background-950/40 px-5 py-2.5 text-sm font-medium text-foreground-100 backdrop-blur-sm transition-colors hover:border-primary-400/70 hover:text-primary-300 md:px-6 md:py-3"
              >
                <i className="ri-information-line text-base" aria-hidden="true" />
                剧集详情
              </Link>
              <span className="hidden items-center gap-1.5 text-xs text-foreground-400 sm:flex">
                <i className="ri-eye-line text-sm" aria-hidden="true" />
                {drama.views} 次播放 · {drama.status}
              </span>
            </div>
          </div>

          <div className="mt-8 flex w-full items-center justify-between gap-4 md:mt-10">
            <div className="flex items-center gap-2" role="tablist" aria-label="推荐位切换">
              {heroSlides.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={index === active}
                  aria-label={`查看第 ${index + 1} 个推荐`}
                  onClick={() => setActive(index)}
                  className={`h-1 cursor-pointer rounded-full transition-all ${
                    index === active ? "w-10 bg-primary-400" : "w-5 bg-foreground-50/30 hover:bg-foreground-50/60"
                  }`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="上一个推荐"
                onClick={() => go(-1)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-foreground-50/20 bg-background-950/40 text-foreground-100 backdrop-blur-sm transition-colors hover:border-primary-400 hover:text-primary-300"
              >
                <i className="ri-arrow-left-s-line text-lg" aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label="下一个推荐"
                onClick={() => go(1)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-foreground-50/20 bg-background-950/40 text-foreground-100 backdrop-blur-sm transition-colors hover:border-primary-400 hover:text-primary-300"
              >
                <i className="ri-arrow-right-s-line text-lg" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}